import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Activity, Users } from "lucide-react";
import {
  AdminOverview, AuditEntry, adminApi, formatMoney, timeAgo,
} from "@/lib/catalog-api";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function AdminDashboard() {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [activity, setActivity] = useState<AuditEntry[] | null>(null);

  useEffect(() => {
    adminApi.overview().then(setOverview).catch(() => setOverview(null));
    adminApi.recentActivity().then(setActivity).catch(() => setActivity([]));
  }, []);

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="admin-page">
      <div className="flex items-baseline gap-3 flex-wrap mb-2">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium">
          — Admin
        </p>
        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Owner / admin only
        </span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl">Admin dashboard</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-prose">
        The control panel for {overview?.workspace_name || "this workspace"}.
      </p>

      {!overview ? (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border" data-testid="admin-stats">
            <Stat label="Items" value={overview.total_items.toLocaleString()} />
            <Stat label="Insured value" value={formatMoney(overview.inventory_value_insured)} />
            <Stat label="Active loans" value={overview.active_loans.toLocaleString()}
              hint={overview.overdue_loans > 0 ? `${overview.overdue_loans} overdue` : undefined} />
            <Stat label="Members" value={`${overview.members_active}`}
              hint={overview.members_pending > 0 ? `${overview.members_pending} pending` : undefined} />
            <Stat label="Outfits" value={overview.total_outfits.toLocaleString()} />
            <Stat label="Clients" value={overview.total_clients.toLocaleString()} />
            <Stat label="Locations" value={overview.total_locations.toLocaleString()} />
            <Stat label="Trial ends" value={overview.trial_days_left == null ? "—" : `${overview.trial_days_left}d`} />
          </div>

          <div className="mt-12 grid lg:grid-cols-[1fr_360px] gap-8">
            <section data-testid="admin-integrity">
              <h2 className="font-display text-xl mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-oxblood" /> Data integrity
              </h2>
              <ul className="divide-y divide-border border-y border-border">
                <IntegrityRow
                  label="Items missing a photo"
                  count={overview.data_integrity.items_without_photo}
                  to="/app/items"
                />
                <IntegrityRow
                  label="Items without a current location"
                  count={overview.data_integrity.items_without_location}
                  to="/app/items"
                />
                <IntegrityRow
                  label="Loans overdue more than 14 days"
                  count={overview.data_integrity.loans_overdue_over_14d}
                  to="/app/loans"
                  bad
                />
                <IntegrityRow
                  label="Clients with zero items"
                  count={overview.data_integrity.clients_without_items}
                  to="/app/clients"
                />
              </ul>

              <div className="mt-10 flex flex-wrap gap-2">
                <Link to="/app/admin/audit-log"
                  data-testid="admin-audit-log-link"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-foreground/20 text-sm hover:bg-bone">
                  <Activity className="w-4 h-4" /> Audit log
                </Link>
                <Link to="/app/settings/team"
                  data-testid="admin-team-link"
                  className="inline-flex items-center gap-2 h-10 px-4 border border-foreground/20 text-sm hover:bg-bone">
                  <Users className="w-4 h-4" /> Team
                </Link>
              </div>
            </section>

            <aside data-testid="admin-recent-activity">
              <h2 className="font-display text-xl mb-4">Recent activity</h2>
              {activity === null ? (
                <Skeleton className="h-40" />
              ) : activity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              ) : (
                <ul className="space-y-3">
                  {activity.slice(0, 12).map((a) => (
                    <li key={a.id} className="text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium">{(a as any).action_label || a.action}</span>
                        <span className="text-[11px] text-muted-foreground">{timeAgo(a.created_at)}</span>
                      </div>
                      <p className="text-[12px] text-muted-foreground truncate">
                        {a.actor_email || "system"}
                        {a.resource_label ? ` · ${a.resource_label}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </aside>
          </div>
        </>
      )}
    </main>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-background p-5 lg:p-6">
      <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="font-display text-2xl lg:text-3xl mt-2">{value}</p>
      {hint && <p className="text-[11px] text-oxblood-deep mt-1">{hint}</p>}
    </div>
  );
}

function IntegrityRow({ label, count, to, bad }: {
  label: string; count: number; to: string; bad?: boolean;
}) {
  return (
    <li className="flex items-center justify-between py-3.5">
      <span className="text-sm">{label}</span>
      <Link to={to} className={`text-sm font-mono tabular-nums ${
        count === 0 ? "text-muted-foreground" : bad ? "text-oxblood" : "text-foreground"
      } hover:underline underline-offset-4`}>
        {count}
      </Link>
    </li>
  );
}
